import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Target, Info, Play, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { AiAnalysisResponse, EnrichedAnalysisV1 } from '@/features/swing-lab/ai/types';
import { extractCanonFaults } from '@/lib/explanations/canon-fault-filter';
import { isCanonFault } from '@/lib/explanations/canon-validator';
import { useAnalysisFrames } from '../hooks/useAnalysisFrames';
import { extractHeadline, extractDescription, sanitizeForUX } from '../utils/text-extraction';
import { buildScoringImpactText } from '../utils/scoring-impact-builder';
import Hls from 'hls.js';
import { supabase } from '@/integrations/supabase/client';
import type { PoseData } from '@/features/telemetry2/types';
import { createGetFrameAtTimeFromPoseData } from '../utils/poseOverlayFromRun';
import OverlayCanvas from '@/features/swing-lab/analysis/OverlayCanvas';

/** Set true to re-enable pose skeleton overlay on analysis video (currently unstable across frames). */
const POSE_OVERLAY_ANALYSIS_ENABLED = false;
import { Button } from '@/components/ui/button';

interface SwingAnalysisCanonProps {
  analysis: AiAnalysisResponse | EnrichedAnalysisV1;
  runId: string;
  videoUrl?: string;
  analyzedAt?: string;
  onBack?: () => void;
  /** PLA-111: Coaching V3 data containing LLM-generated focus title */
  coachingData?: {
    coaching_mode?: 'fault_focused' | 'execution_maintenance';
    summary?: string;
    confidence?: number;
    recommendations?: {
      primary_focus?: string;
      swing_thoughts?: string[];
      drills?: Array<{
        drill_id?: string;
        title?: string;
        why?: string;
        instructions?: string[];
        sets_reps?: string;
      }>;
    };
    practice_plan?: {
      focus_areas?: string[];
      weekly_schedule?: Array<{
        day?: string;
        drills?: string[];
      }>;
    };
    single_priority_focus?: {
      priority_flaw_id?: string | null;
      why_priority?: string;
    };
  } | null;
  /** Pose/telemetry data for skeleton overlay on video */
  poseData?: PoseData | null;
}

/**
 * PLA-107, PLA-113: Primary Focus from the Prioritizer (Canon Control Signal)
 * This is the AUTHORITATIVE decision on what to focus on (or null for execution mode)
 * 
 * PLA-113: NO rationale - Canon is linguistically mute
 * The Coaching Agent generates all explanatory language
 */
interface PrimaryLever {
  fault_id: string | null;
  confidence_band: 'low' | 'medium' | 'high';
}

interface CanonicalAnalysisData {
  runId: string;
  score: number | null;
  scoreInterpretation: string | null;
  primaryFault: {
    faultId: string; // Canonical fault ID (e.g., "early_extension") - INTERNAL ONLY
    canonicalName: string; // Fallback display name (e.g., "Early Extension")
    /** PLA-111: LLM-generated focus title from coaching agent - PRIMARY display */
    coachingFocusTitle: string | null;
    /** PLA-113: From coaching agent summary, NOT from Canon */
    headline: string | null;
    region: 'impact' | 'backswing' | 'downswing' | 'follow-through';
    /** PLA-113: From coaching agent summary, NOT from Canon */
    description: string | null;
    explanation: {
      /** PLA-113: From coaching agent, NOT from Canon (Canon is linguistically mute) */
      why: string[];
      affects: string[];
    };
  } | null; // null when no primary fault exists
  positiveObservations: string[];
  scoringImpact: {
    impactItems: string[]; // Synthesized qualitative risk explanation (deterministic)
  } | null; // null when no primary fault exists
  videoUrl: string;
  faultFrame?: {
    imageUrl: string;
    timestamp: string;
    region: 'impact' | 'backswing' | 'downswing' | 'follow-through';
  };
  analyzedAt: string;
  // PLA-94: Track if this analysis is rendered via phase evidence only (no root causes)
  isPhaseEvidenceOnly?: boolean;
  // PLA-99: Track if this is an execution-only swing (no faults, structurally sound)
  isExecutionOnly?: boolean;
  // PLA-107: Primary lever from Prioritizer (authoritative)
  primaryLever?: PrimaryLever;
  // PLA-117: Execution-only coaching (still show coaching feedback even when no fault is found)
  executionCoaching?: {
    focusTitle: string | null;
    headline: string | null;
    description: string | null;
    whyPriority: string | null;
    impactIfAddressed: string | null;
    swingThoughts: string[];
    practicePlan: {
      focusAreas: string[];
      weeklySchedule: Array<{ day: string; drills: string[] }>;
    } | null;
  };
}

// Map region from fault explanation or analysis data
function mapRegion(faultId: string, analysis: any): 'impact' | 'backswing' | 'downswing' | 'follow-through' {
  // Try to extract from phase summaries first
  if (analysis.phase_summaries) {
    for (const phase of analysis.phase_summaries) {
      for (const obs of phase.observations || []) {
        if (obs.flaw_id?.toLowerCase() === faultId.toLowerCase()) {
          const phaseName = phase.phase?.toLowerCase() || '';
          if (phaseName.includes('impact')) return 'impact';
          if (phaseName.includes('downswing')) return 'downswing';
          if (phaseName.includes('backswing')) return 'backswing';
          if (phaseName.includes('follow')) return 'follow-through';
        }
      }
    }
  }
  
  // Default mapping based on fault ID
  const id = faultId.toLowerCase();
  if (id.includes('impact') || id.includes('contact')) return 'impact';
  if (id.includes('downswing') || id.includes('transition')) return 'downswing';
  if (id.includes('backswing') || id.includes('takeaway')) return 'backswing';
  if (id.includes('follow') || id.includes('finish')) return 'follow-through';
  
  return 'impact'; // Default fallback
}

// Format timestamp from seconds to "00:00:01.500" format
function formatTimestamp(seconds?: number): string {
  if (!seconds) return '00:00:00.000';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

// Format analyzed date
function formatAnalyzedAt(dateString?: string): string {
  if (!dateString) return 'recently';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Format fault ID to display name (e.g., early_extension → Early Extension)
function formatFaultDisplayName(faultId: string): string {
  const displayNames: Record<string, string> = {
    early_extension: 'Early Extension',
    loss_of_spine_angle: 'Loss of Spine Angle',
    sway: 'Sway',
    reverse_pivot: 'Reverse Pivot',
    hanging_back: 'Hanging Back',
    limited_rotation: 'Limited Rotation',
    casting: 'Casting',
    early_upper_body_transition: 'Early Upper Body Transition',
    trail_knee_straightening: 'Trail Knee Straightening',
    pelvis_thrust: 'Pelvis Thrust',
    arm_depth_loss: 'Arm Depth Loss',
    chicken_wing: 'Chicken Wing',
    weight_stuck_trail: 'Weight Stuck on Trail Foot',
    standing_up: 'Standing Up',
    over_the_top: 'Over the Top',
    slide: 'Slide',
    shoulder_turn_insufficient: 'Insufficient Shoulder Turn',
    hip_rotation_insufficient: 'Insufficient Hip Rotation',
  };
  
  const normalized = faultId.toLowerCase().trim();
  return displayNames[normalized] || normalized
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// PLA-118: UI hygiene — remove exact consecutive duplicate sentences (common LLM hiccup).
function dedupeConsecutiveSentences(text: string): string {
  const raw = (text || '').trim();
  if (!raw) return raw;

  // Split on sentence boundaries; keep punctuation.
  const parts = raw.split(/(?<=[.!?])\s+/g).map(s => s.trim()).filter(Boolean);
  if (parts.length < 2) return raw;

  const out: string[] = [];
  for (const p of parts) {
    if (out.length === 0 || out[out.length - 1] !== p) out.push(p);
  }
  return out.join(' ');
}

// PLA-118: UI hygiene — prevent Key Opportunity description from re-stating the headline.
function stripLeadingDuplicateHeadline(headline: string | null, description: string | null): string | null {
  if (!headline || !description) return description;
  const h = headline.trim();
  let d = description.trim();
  if (!h || !d) return description;

  // Exact prefix match (most common case).
  if (d.startsWith(h)) {
    d = d.slice(h.length).trim();
    d = d.replace(/^[-–—:;\s]+/, '').trim();
  }

  return d.length > 0 ? d : null;
}

async function resolveSignedFrameUrl(raw: string | null | undefined): Promise<string | null> {
  const v = (raw || '').trim();
  if (!v) return null;
  if (v.startsWith('http') && !v.includes('/storage/v1/object/public/')) return v;

  // bucket:path
  const m = v.match(/^([a-z0-9-]+):(.*)$/i);
  if (m && m[1] && m[2]) {
    const bucket = m[1];
    const path = m[2].replace(/^\/+/, '');
    const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
    return data?.signedUrl || v;
  }

  // public URL (parseable even if private bucket returns 404)
  const publicMarker = '/storage/v1/object/public/';
  const idx = v.indexOf(publicMarker);
  if (idx !== -1) {
    const rest = v.slice(idx + publicMarker.length);
    const [bucket, ...pathParts] = rest.split('/');
    const path = pathParts.join('/');
    if (bucket && path) {
      const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
      return data?.signedUrl || v;
    }
  }

  // bare path fallback (assume swing-lab)
  if (!v.startsWith('http') && v.includes('/')) {
    const { data } = await supabase.storage.from('swing-lab').createSignedUrl(v.replace(/^\/+/, ''), 60 * 60);
    return data?.signedUrl || v;
  }

  return v;
}

export function SwingAnalysisCanon({ 
  analysis, 
  runId, 
  videoUrl = '', 
  analyzedAt,
  onBack,
  coachingData,
  poseData
}: SwingAnalysisCanonProps) {
  const navigate = useNavigate();
  const [canonicalData, setCanonicalData] = useState<CanonicalAnalysisData | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [videoElementReady, setVideoElementReady] = useState(false);
  const [signedFaultFrameUrl, setSignedFaultFrameUrl] = useState<string | null>(null);
  const [refreshingFaultFrame, setRefreshingFaultFrame] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayOpacity] = useState(80);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const getFrameAtTimeFromPose = useMemo(
    () => createGetFrameAtTimeFromPoseData(poseData ?? null),
    [poseData]
  );
  const getLatestFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video) return null;
    return getFrameAtTimeFromPose(video.currentTime) ?? null;
  }, [getFrameAtTimeFromPose]);
  
  // Fetch frames from analysis_frames table as fallback
  const { frames: analysisFrames } = useAnalysisFrames(runId);

  // Reset video element ready state when videoUrl changes
  useEffect(() => {
    setVideoElementReady(false);
  }, [videoUrl]);

  // Callback ref to track when video element is mounted
  const videoRefCallback = (element: HTMLVideoElement | null) => {
    videoRef.current = element;
    setVideoElementReady(!!element);
    if (element) {
      console.log('[SwingAnalysisCanon] Video element mounted');
    } else {
      console.log('[SwingAnalysisCanon] Video element unmounted');
    }
  };

  // Initialize HLS.js for Cloudflare Stream playback
  useEffect(() => {
    console.log('[SwingAnalysisCanon] HLS init effect triggered', {
      hasVideoUrl: !!videoUrl,
      videoUrl,
      endsWithM3u8: videoUrl?.endsWith('.m3u8'),
      hasVideoRef: !!videoRef.current,
      videoElementReady,
    });

    if (!videoUrl) {
      console.log('[SwingAnalysisCanon] No videoUrl, skipping HLS init');
      return;
    }

    if (!videoElementReady || !videoRef.current) {
      console.log('[SwingAnalysisCanon] Video element not ready yet');
      return;
    }

    // Check if this is a Cloudflare Stream URL
    const isCloudflareUrl = videoUrl.includes('cloudflarestream.com') || videoUrl.includes('videodelivery.net');
    const isHlsUrl = videoUrl.endsWith('.m3u8');
    
    // For Cloudflare URLs, we need to ensure we have the HLS manifest URL
    let hlsVideoUrl = videoUrl;
    if (isCloudflareUrl && !isHlsUrl) {
      // Try to construct HLS URL if it's a Cloudflare URL without .m3u8
      const uidMatch = videoUrl.match(/\/([a-f0-9]{32})(?:\/|$)/i);
      if (uidMatch) {
        const uid = uidMatch[1];
        // Extract base URL (e.g., https://customer-xxx.cloudflarestream.com)
        const urlParts = videoUrl.split('/');
        const baseUrl = `${urlParts[0]}//${urlParts[2]}`;
        hlsVideoUrl = `${baseUrl}/${uid}/manifest/video.m3u8`;
        console.log('[SwingAnalysisCanon] Constructed HLS URL from Cloudflare URL:', {
          original: videoUrl,
          constructed: hlsVideoUrl,
        });
      } else {
        console.warn('[SwingAnalysisCanon] Cloudflare URL but could not extract UID:', videoUrl);
        return;
      }
    } else if (!isHlsUrl && !isCloudflareUrl) {
      // Not an HLS URL and not Cloudflare - skip HLS initialization
      console.log('[SwingAnalysisCanon] Not an HLS URL, skipping HLS init:', videoUrl);
      return;
    }

    const video = videoRef.current;
    if (!video) {
      console.warn('[SwingAnalysisCanon] Video element is null');
      return;
    }

    console.log('[SwingAnalysisCanon] Initializing HLS playback:', {
      originalVideoUrl: videoUrl,
      hlsVideoUrl,
      isCloudflareUrl,
      hlsSupported: Hls.isSupported(),
      videoElementReady: !!video,
    });

    // Cleanup previous instance
    if (hlsRef.current) {
      console.log('[SwingAnalysisCanon] Cleaning up previous HLS instance');
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Check for HLS support
    if (Hls.isSupported()) {
      console.log('[SwingAnalysisCanon] HLS.js is supported, creating instance');
      const hls = new Hls({
        debug: true, // Enable debug logging
        enableWorker: true,
      });
      
      hls.loadSource(hlsVideoUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('[SwingAnalysisCanon] ✅ HLS manifest loaded successfully');
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[SwingAnalysisCanon] ❌ HLS error:', {
          event,
          type: data.type,
          details: data.details,
          fatal: data.fatal,
          error: data.error,
        });
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('[SwingAnalysisCanon] Fatal network error, trying to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('[SwingAnalysisCanon] Fatal media error, trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.log('[SwingAnalysisCanon] Fatal error, destroying HLS...');
              hls.destroy();
              hlsRef.current = null;
              break;
          }
        }
      });
      
      hlsRef.current = hls;
      console.log('[SwingAnalysisCanon] HLS instance created and attached');
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari supports HLS natively
      console.log('[SwingAnalysisCanon] Using native HLS support (Safari)');
      video.src = hlsVideoUrl;
    } else {
      console.error('[SwingAnalysisCanon] ❌ HLS not supported in this browser');
    }

    // Cleanup on unmount
    return () => {
      if (hlsRef.current) {
        console.log('[SwingAnalysisCanon] Cleaning up HLS on unmount');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, videoElementReady]);

  // NOTE: Avoid custom click-to-play overlays here.
  // Native controls are more reliable and must remain usable (especially on mobile).

  // Extract canonical data from analysis
  useEffect(() => {
    // PLA-89: V3 Authoritative Rendering Contract
    // REQUIRED for rendering:
    // - primary_fault_id OR global_priorities[0]
    // - root_causes.length >= 1
    //
    // EXPLICITLY OPTIONAL (must NOT gate rendering):
    // - explanations
    // - compensation_chains
    // - classification.primary_pattern
    // - fault_frames
    // - Any scoring or estimate fields
    
    // PLA-89: V3_RENDER_AUDIT diagnostic console block
    // No gating. No branching. No UI behavior change.
    const rootCauses = (analysis as any).root_causes || [];
    const compensationChains = (analysis as any).compensation_chains || [];
    const phaseSummaries = (analysis as any).phase_summaries || [];
    const taxonomyVersion = (analysis as any).taxonomy_version || 'unknown';
    const auditPrimaryFaultId = (analysis as any).primary_fault_id || (analysis as any).global_priorities?.[0] || null;
    
    console.group('[V3_RENDER_AUDIT]');
    console.log({
      runId,
      primaryFaultId: auditPrimaryFaultId,
      rootCausesCount: rootCauses.length,
      compensationChainsCount: compensationChains.length,
      hasPhaseSummaries: phaseSummaries.length > 0,
      taxonomyVersion,
    });
    console.groupEnd();
    
    const isEnriched = 'taxonomy_version' in analysis;
    const enrichedAnalysis = isEnriched ? analysis as EnrichedAnalysisV1 : null;
    
    // Extract canon faults from phase_summaries for fallback
    const enrichedAnalysisData = enrichedAnalysis?.phase_summaries 
      ? { phase_summaries: enrichedAnalysis.phase_summaries }
      : null;
    
    const canonFaults = enrichedAnalysisData 
      ? extractCanonFaults(enrichedAnalysisData)
      : [];
    
    // Filter to only valid canon faults (no explanation check needed - V3 data is source of truth)
    const validCanonFaults = canonFaults.filter(fault => {
      const isValid = isCanonFault(fault.fault_id);
      if (!isValid) {
        console.warn('[SwingAnalysisCanon] Skipping non-canon fault:', fault.fault_id);
        return false;
      }
      return true;
    });
    
    // PLA-88: Determine primary fault in priority order (authoritative contract):
    // 1. primary_fault_id (authoritative contract from backend)
    // 2. global_priorities[0] (backward compatible fallback)
    // 3. Render empty state
    let primaryFaultId = '';
    
    // Priority 1: Use authoritative primary_fault_id from backend contract
    if ((analysis as any).primary_fault_id && typeof (analysis as any).primary_fault_id === 'string') {
      primaryFaultId = String((analysis as any).primary_fault_id).toLowerCase().trim();
      
      // PLA-88: Diagnostic logging for validator mismatch (warn only, do not block render)
      if (!isCanonFault(primaryFaultId)) {
        console.warn('[SwingAnalysisCanon] primary_fault_id exists but isCanonFault() returns false', {
          primary_fault_id: primaryFaultId,
          primary_fault_source: (analysis as any).primary_fault_source,
          runId
        });
      }
    }
    
    // Priority 2: Fallback to global_priorities[0] for backward compatibility
    if (!primaryFaultId && (analysis as any).global_priorities && Array.isArray((analysis as any).global_priorities) && (analysis as any).global_priorities.length > 0) {
      primaryFaultId = String((analysis as any).global_priorities[0]).toLowerCase().trim();
      
      // PLA-88: Diagnostic logging for validator mismatch (warn only, do not block render)
      if (!isCanonFault(primaryFaultId)) {
        console.warn('[SwingAnalysisCanon] global_priorities[0] used but isCanonFault() returns false', {
          global_priorities_0: primaryFaultId,
          runId
        });
      }
    }
    
    // Extract score
    const score = enrichedAnalysis?.swing_quality ?? null;
    
    // Extract score interpretation from V3 classification
    const scoreInterpretation = (analysis as any).classification?.summary || null;
    
    // Extract positive observations from phase_summaries where severity === 0 (good)
    // Note: consistency_indicators contains negative/neutral traits, not positive ones
    // PLA-116: Block analysis.reasoning from UI - use evidence only (factual observation)
    const positiveObservations = (analysis as any).phase_summaries
      ?.flatMap((phase: any) => phase.observations || [])
      .filter((obs: any) => obs.severity === 0)
      .map((obs: any) => {
        // PLA-116: Use evidence only, NOT reasoning (reasoning is internal analysis)
        return (obs.evidence && obs.evidence[0]) || null;
      })
      .filter(Boolean) || [];
    
    // PLA-117: Execution-only / maintenance mode detection
    // Prefer the coaching output (authoritative for UI mode gating), then fall back to analysis fields.
    const coachingMode = coachingData?.coaching_mode;
    const isExecutionOnlyFromCoaching = coachingMode === "execution_maintenance";
    const primaryFaultSource = String((analysis as any).primary_fault_source || "");
    const isExecutionOnlyFromPrimaryFaultSource = [
      "execution_only",
      "null_state_elite",
      "phase_2_prioritizer_null",
      "phase_2_prioritizer_confidence_null",
    ].includes(primaryFaultSource);
    const isExecutionOnlyLegacy = (analysis as any).analysis_type === "execution_only";
    const isExecutionOnly = isExecutionOnlyFromCoaching || isExecutionOnlyFromPrimaryFaultSource || isExecutionOnlyLegacy;
    
    if (isExecutionOnlyFromCoaching) {
      console.log('[SwingAnalysisCanon] PLA-117: Execution mode detected via coaching_data_v3.coaching_mode', {
        runId,
        coaching_mode: coachingMode
      });
    }
    
    // PLA-94: V3 Authoritative Rendering Contract (Relaxed)
    // A V3 analysis is renderable if:
    // 1. analysis_type === "execution_only" (PLA-99: valid no-fault swing)
    // 2. OR primaryFaultId exists (via primary_fault_id OR global_priorities[0])
    //    AND at least ONE of the following is true:
    //    a) root_causes is an array with length >= 1
    //    b) phase_summaries has observations (phase evidence)
    //
    // This allows canon-valid analyses that resolve faults from phase evidence alone
    // without requiring causal attribution via root_causes.
    const hasRootCauses = Array.isArray(rootCauses) && rootCauses.length > 0;
    
    // PLA-94: Check for phase-level evidence supporting the fault
    const hasPhaseEvidence = Array.isArray(phaseSummaries) &&
      phaseSummaries.some(
        (p: any) => Array.isArray(p.observations) && p.observations.length > 0
      );
    
    // PLA-99: Execution-only swings are renderable even without faults
    const hasRenderableAnalysis = isExecutionOnly || (!!primaryFaultId && (hasRootCauses || hasPhaseEvidence));
    
    // PLA-94: Track whether this is a phase-evidence-only render (no root causes)
    const isPhaseEvidenceOnly = !!primaryFaultId && hasPhaseEvidence && !hasRootCauses;
    
    if (isPhaseEvidenceOnly) {
      console.log('[SwingAnalysisCanon] Rendering via phase evidence (no root causes)', {
        primaryFaultId,
        phaseObservationCount: phaseSummaries.reduce(
          (acc: number, p: any) => acc + (p.observations?.length || 0), 0
        ),
        runId
      });
    }
    
    // PLA-99: Log execution-only swings
    if (isExecutionOnly) {
      console.log('[SwingAnalysisCanon] PLA-99: Rendering execution-only swing (no faults)', {
        analysis_type: (analysis as any).analysis_type,
        runId
      });
    }

    if (!hasRenderableAnalysis) {
      console.warn('[SwingAnalysisCanon] Analysis not renderable', {
        hasPrimaryFaultId: !!primaryFaultId,
        hasRootCauses,
        hasPhaseEvidence,
        runId
      });
      setCanonicalData({
        runId,
        score,
        scoreInterpretation,
        primaryFault: null,
        positiveObservations,
        scoringImpact: null,
        videoUrl,
        faultFrame: undefined,
        analyzedAt: formatAnalyzedAt(analyzedAt),
        isPhaseEvidenceOnly: false,
        isExecutionOnly: false,
      });
      return;
    }
    
    // PLA-99/PLA-117: Handle execution-only swings with dedicated rendering path
    // Still allow coaching feedback to flow through (summary + swing thoughts + drills + practice plan).
    if (isExecutionOnly) {
      const rawExecSummary = coachingData?.summary || null;
      const execSummary = sanitizeForUX(rawExecSummary, { runId, field: 'coachingData.summary' });
      const execHeadline = extractHeadline(execSummary);
      const execDescription = extractDescription(execSummary, execHeadline);
      
      const focusTitle = coachingData?.recommendations?.primary_focus || null;
      const whyPriorityRaw = coachingData?.single_priority_focus?.why_priority || null;
      const impactIfAddressedRaw = coachingData?.root_cause_justification?.impact_if_addressed || null;
      const whyPriority = sanitizeForUX(
        typeof whyPriorityRaw === 'string' ? whyPriorityRaw : null,
        { runId, field: 'coachingData.single_priority_focus.why_priority' }
      );
      const impactIfAddressed = sanitizeForUX(
        typeof impactIfAddressedRaw === 'string' ? impactIfAddressedRaw : null,
        { runId, field: 'coachingData.root_cause_justification.impact_if_addressed' }
      );
      
      const swingThoughts = (coachingData?.recommendations?.swing_thoughts || [])
        .filter((t): t is string => typeof t === "string")
        .map(t => sanitizeForUX(t, { runId, field: 'coachingData.recommendations.swing_thoughts' }))
        .filter((t): t is string => !!t);
      
      const practicePlan =
        coachingData?.practice_plan
          ? {
              focusAreas: Array.isArray(coachingData.practice_plan.focus_areas)
                ? coachingData.practice_plan.focus_areas.filter((a): a is string => typeof a === "string" && a.trim().length > 0)
                : [],
              weeklySchedule: Array.isArray(coachingData.practice_plan.weekly_schedule)
                ? coachingData.practice_plan.weekly_schedule
                    .filter((d): d is any => d && typeof d === "object")
                    .map(d => ({
                      day: typeof d.day === "string" ? d.day : "Day",
                      drills: Array.isArray(d.drills) ? d.drills.filter((x): x is string => typeof x === "string" && x.trim().length > 0) : [],
                    }))
                : [],
            }
          : null;
      
      setCanonicalData({
        runId,
        score,
        scoreInterpretation,
        primaryFault: null, // No fault for execution-only
        positiveObservations,
        scoringImpact: null, // No scoring impact for execution-only
        videoUrl,
        faultFrame: undefined,
        analyzedAt: formatAnalyzedAt(analyzedAt),
        isPhaseEvidenceOnly: false,
        isExecutionOnly: true,
        executionCoaching: {
          focusTitle,
          headline: execHeadline,
          description: execDescription,
          whyPriority: whyPriority || null,
          impactIfAddressed: impactIfAddressed || null,
          swingThoughts,
          practicePlan,
        },
      });
      return;
    }
    
    // PLA-107, PLA-113: Extract primary_lever from analysis (authoritative from Prioritizer)
    // PLA-113: Canon is linguistically mute - NO rationale
    // "Why This Happens" comes from Coaching Agent, not Canon
    const primaryLever: PrimaryLever | undefined = (analysis as any).primary_lever;
    const contributingRootCauses = (analysis as any).root_causes || [];
    
    // PLA-115: Narrative Ownership - SINGLE source of truth
    // Key Opportunity: coaching.summary (via headline/description) - ONE causal explanation
    // Why This Happens: why_priority (reference-only, 1 sentence) - NOT a re-explanation
    
    // Source 1: rawJustification → headline/description for Key Opportunity
    let rawJustification: string | null = null;
    
    // Source 2: why[] → "Why This Happens" card (reference only, NOT summary)
    let why: string[] = [];
    
    // PLA-115: Summary goes to Key Opportunity ONLY (via headline/description)
    if (coachingData?.summary) {
      rawJustification = dedupeConsecutiveSentences(coachingData.summary);
      
      console.log('[SwingAnalysisCanon] PLA-115: Using coaching summary for Key Opportunity', {
        runId,
        fault_id: primaryLever?.fault_id,
        confidence_band: primaryLever?.confidence_band,
        summary_length: coachingData.summary?.length || 0
      });
    }
    
    // PLA-115/PLA-118: "Why This Happens" uses why_priority (reference-only, not full explanation)
    // Guardrail: suppress if it duplicates the summary or is too long.
    if (coachingData?.single_priority_focus?.why_priority) {
      const sanitizedWhyPriority = sanitizeForUX(
        coachingData.single_priority_focus.why_priority, 
        { runId, field: 'coachingData.single_priority_focus.why_priority' }
      );
      const sanitizedSummary = sanitizeForUX(coachingData?.summary || null, { runId, field: 'coachingData.summary' });
      const whyCandidate = sanitizedWhyPriority?.trim() || '';
      const summaryCandidate = sanitizedSummary?.trim() || '';

      const tooLong = whyCandidate.length > 220;
      const looksDuplicate =
        whyCandidate.length > 0 &&
        summaryCandidate.length > 0 &&
        (summaryCandidate.includes(whyCandidate) || whyCandidate.includes(summaryCandidate));

      if (sanitizedWhyPriority && !tooLong && !looksDuplicate) {
        why = [sanitizedWhyPriority];
      }
      
      console.log('[SwingAnalysisCanon] PLA-115: Using why_priority for Why This Happens', {
        runId,
        fault_id: primaryLever?.fault_id,
        why_priority_length: coachingData.single_priority_focus.why_priority?.length || 0
      });
    }
    
    // PLA-116: Block analysis text from UI - coaching data ONLY
    // If no coaching data exists, use graceful fallback (do NOT render analysis internals)
    if (!coachingData?.summary && !coachingData?.single_priority_focus?.why_priority) {
      // PLA-116: Graceful fallback - do NOT use analysis.justification or phase.reasoning
      // These are internal fields that should never reach the golfer
      rawJustification = null;
      // why stays empty - no fallback to root_cause justifications or causal_chain evidence
      
      console.log('[SwingAnalysisCanon] PLA-116: No coaching data available, using graceful fallback', {
        runId,
        fault_id: primaryLever?.fault_id,
        coaching_available: false
      });
    }
    
    const justification = sanitizeForUX(rawJustification, { runId, field: 'justification' });
    const headline = extractHeadline(justification);
    const description = stripLeadingDuplicateHeadline(
      headline,
      extractDescription(justification, headline)
    );
    
    const primaryFaultDisplayName = formatFaultDisplayName(primaryFaultId);
    
    // Extract "How It Affects" from compensation_chains
    // This is semantically correct - describes compensatory behavior
    const affectsItems = (analysis as any).compensation_chains
      ?.filter((cc: any) => 
        cc.primary_flaw_id?.toLowerCase() === primaryFaultId.toLowerCase()
      )
      .map((cc: any) => cc.evidence)
      .filter(Boolean) || [];
    
    // PLA-120 (Option A): Prefer Coaching output for "Why This Costs You Strokes" when available.
    // Do NOT rewrite/post-process this text; use verbatim.
    const coachingImpactIfAddressedRaw =
      coachingData?.root_cause_justification?.impact_if_addressed;
    const coachingImpactIfAddressed =
      typeof coachingImpactIfAddressedRaw === 'string' && coachingImpactIfAddressedRaw.trim().length > 0
        ? coachingImpactIfAddressedRaw.trim()
        : null;

    // PLA-96: Build synthesized scoring impact text using PRIMARY FAULT, not root cause
    // This ensures "Why This Costs You Strokes" describes the actionable pattern's risk
    const scoringImpactText = coachingImpactIfAddressed || buildScoringImpactText(
        primaryFaultId,  // PLA-96: Pass primary fault ID explicitly
        contributingRootCauses,
        (analysis as any).compensation_chains || [],
        (analysis as any).phase_summaries || []
      );
    
    // Detect duplicate content between "How It Affects" and scoring impact
    if (scoringImpactText && affectsItems.length > 0) {
      const scoringLower = scoringImpactText.toLowerCase();
      const hasDuplicate = affectsItems.some((item: string) => {
        const itemLower = item.toLowerCase();
        // Check if scoring impact contains compensation evidence verbatim
        return scoringLower.includes(itemLower) || itemLower.includes(scoringLower);
      });
      
      if (hasDuplicate) {
        console.warn('[SwingAnalysisCanon] Potential duplicate content detected between "How It Affects" and "Why This Costs You Strokes"');
      }
    }
    
    // Map region
    const region = mapRegion(primaryFaultId, analysis);
    
    // Generate canonical display name (fallback)
    const canonicalName = formatFaultDisplayName(primaryFaultId);
    
    // PLA-111: Extract LLM-generated focus title from coaching data
    // This is the PRIMARY display, canonicalName is fallback
    const coachingFocusTitle = coachingData?.recommendations?.primary_focus || null;
    
    // Extract fault frame if present (from analysis data or analysis_frames table)
    let faultFrame: CanonicalAnalysisData['faultFrame'] | undefined;
    
    // First try: extract from analysis_data_v3.faultFrame
    if ((analysis as any).faultFrame) {
      const ff = (analysis as any).faultFrame;
      faultFrame = {
        imageUrl: ff.imageUrl || ff.image_url || '',
        timestamp: ff.timestamp || formatTimestamp(ff.timestampSeconds),
        region: ff.region || region,
      };
    }
    // Fallback: try to get from analysis_frames table
    else if (analysisFrames[primaryFaultId]) {
      const frame = analysisFrames[primaryFaultId];
      faultFrame = {
        imageUrl: frame.image_url,
        timestamp: formatTimestamp(frame.timestamp_ms ? frame.timestamp_ms / 1000 : undefined),
        region: region,
      };
    }
    
    setCanonicalData({
      runId,
      score,
      scoreInterpretation,
      primaryFault: {
        faultId: primaryFaultId,
        canonicalName,
        coachingFocusTitle, // PLA-111: LLM-generated title (primary display)
        headline,
        region,
        description,
        explanation: {
          why: why.length > 0 ? why : [],
          affects: affectsItems.length > 0 ? affectsItems : [],
        },
      },
      positiveObservations,
      scoringImpact: scoringImpactText ? {
        impactItems: [scoringImpactText],  // Single synthesized paragraph
      } : null,
      videoUrl,
      faultFrame,
      analyzedAt: formatAnalyzedAt(analyzedAt),
      isPhaseEvidenceOnly,  // PLA-94: Track phase-evidence-only renders
      isExecutionOnly: false,  // PLA-99: Not execution-only (has faults)
      // PLA-107: Include primary lever for downstream use
      primaryLever: primaryLever,
    });
    
    // PLA-89: Diagnostic logging for optional fields (non-blocking)
    if (!(analysis as any).explanations || (analysis as any).explanations.length === 0) {
      console.log('[SwingAnalysisCanon] Optional field missing: explanations', { runId });
    }
    if (!(analysis as any).compensation_chains || (analysis as any).compensation_chains.length === 0) {
      console.log('[SwingAnalysisCanon] Optional field missing: compensation_chains', { runId });
    }
  }, [analysis, runId, videoUrl, analyzedAt, analysisFrames, coachingData]);

  // Resolve signed URL for fault frame image (Storage buckets may be private).
  useEffect(() => {
    const raw = canonicalData?.faultFrame?.imageUrl || null;
    let cancelled = false;
    (async () => {
      const signed = await resolveSignedFrameUrl(raw);
      if (!cancelled) setSignedFaultFrameUrl(signed);
    })();
    return () => {
      cancelled = true;
    };
  }, [canonicalData?.faultFrame?.imageUrl]);

  const scrollToVideo = () => {
    const videoElement = document.getElementById('swing-video');
    videoElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const parseTimestampToSeconds = (value: string | undefined | null): number | null => {
    if (!value) return null;
    const parts = value.split(':').map(p => p.trim());
    const last = parts[parts.length - 1];
    const sec = Number(last);
    if (Number.isNaN(sec)) return null;

    if (parts.length === 1) return sec;
    const min = Number(parts[parts.length - 2]);
    if (Number.isNaN(min)) return null;
    if (parts.length === 2) return min * 60 + sec;
    const hr = Number(parts[parts.length - 3]);
    if (Number.isNaN(hr)) return null;
    return hr * 3600 + min * 60 + sec;
  };

  const handleFaultFrameClick = () => {
    scrollToVideo();
    const video = videoRef.current;
    if (!video) return;

    const ts = parseTimestampToSeconds(canonicalData?.faultFrame?.timestamp);
    if (ts === null) return;

    // UX: Seek to the analyzed moment but keep the video paused so the golfer can inspect the frame.
    // (Do NOT auto-play on fault-frame click.)
    video.pause();

    const seekAndPause = () => {
      try {
        video.currentTime = ts;
      } catch {
        // ignore
      }
      // Ensure we remain paused after seeking.
      requestAnimationFrame(() => {
        video.pause();
      });
    };

    if (video.readyState >= 1) {
      seekAndPause();
    } else {
      video.addEventListener('loadedmetadata', seekAndPause, { once: true });
      try {
        video.load();
      } catch {
        // ignore
      }
    }
  };

  const refreshFaultFrame = async () => {
    const primaryFaultId = canonicalData?.primaryFault?.faultId;
    if (!primaryFaultId || !runId) return;

    setRefreshingFaultFrame(true);
    try {
      const faultId = primaryFaultId.toLowerCase().trim();
      const { data, error } = await supabase.functions.invoke('extract-analysis-frame', {
        body: {
          analysis_run_id: runId,
          fault_id: faultId,
        }
      });

      if (error) {
        console.error('[SwingAnalysisCanon] Failed to refresh fault frame', { runId, faultId, error: error.message });
        return;
      }

      const imageUrlRaw = typeof (data as any)?.image_url === 'string' ? (data as any).image_url : null;
      const frameIndex = typeof (data as any)?.frame_index === 'number' ? (data as any).frame_index : null;
      const timestampMs = typeof (data as any)?.timestamp_ms === 'number' ? (data as any).timestamp_ms : null;
      const timestamp = typeof (data as any)?.timestamp_formatted === 'string' ? (data as any).timestamp_formatted : null;
      const region = typeof (data as any)?.region === 'string' ? (data as any).region : null;

      if (!imageUrlRaw || frameIndex === null || timestampMs === null) {
        console.warn('[SwingAnalysisCanon] Refresh fault frame returned unexpected payload', { runId, faultId, data });
        return;
      }

      setCanonicalData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          faultFrame: {
            imageUrl: imageUrlRaw,
            frameIndex,
            fps: prev.faultFrame?.fps || 30,
            timestampMs,
            timestamp: timestamp || prev.faultFrame?.timestamp || '00:00:00.000',
            region: (region as any) || prev.faultFrame?.region || 'impact',
          }
        };
      });

      // Re-resolve signed URL for the new image
      setSignedFaultFrameUrl(null);
    } finally {
      setRefreshingFaultFrame(false);
    }
  };

  const primaryFault = canonicalData?.primaryFault ?? null;
  const faultFrame = canonicalData?.faultFrame;
  const hasPrimaryFault = primaryFault !== null;
  const execWhyPriority = canonicalData?.isExecutionOnly ? canonicalData?.executionCoaching?.whyPriority : null;
  const execImpactIfAddressed = canonicalData?.isExecutionOnly ? canonicalData?.executionCoaching?.impactIfAddressed : null;

  return (
    <div className="bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/videos')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group"
            aria-label="Back to Videos"
          >
            <ArrowLeft className="w-5 h-5 text-[#0D382A] group-hover:-translate-x-0.5 transition-transform" />
            <span className="text-[#0D382A] font-medium">Back to Videos</span>
          </button>
          <div className="ml-auto">
            <h1 className="text-[#0D382A] text-2xl">Swing Analysis</h1>
            <p className="text-gray-500 text-sm">
              Analyzed {canonicalData?.analyzedAt ?? formatAnalyzedAt(analyzedAt)}
            </p>
            {canonicalData?.scoreInterpretation && (
              <p className="text-gray-600 text-sm mt-1">
                {canonicalData.scoreInterpretation}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6 pb-24">
        {/* Video Preview */}
        <div className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(13,56,42,0.12)] overflow-hidden border-2 border-transparent bg-clip-padding relative">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] opacity-100 -z-10" style={{ padding: '2px' }}>
            <div className="absolute inset-[2px] bg-white rounded-2xl" />
          </div>
          
          <div className="relative z-10 rounded-xl overflow-hidden m-[2px]">
            <div 
              className="relative bg-black"
              id="swing-video"
            >
              {/* Top video player must mount once and never be conditionally removed/swapped */}
              <video
                ref={videoRefCallback}
                className="w-full h-auto object-contain bg-black"
                playsInline
                controls={true}
                preload="metadata"
                // For Cloudflare/videodelivery or .m3u8, HLS.js will attach. For direct MP4/etc, native src works.
                src={
                  videoUrl && !(videoUrl.endsWith('.m3u8') || videoUrl.includes('cloudflarestream.com') || videoUrl.includes('videodelivery.net'))
                    ? videoUrl
                    : undefined
                }
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onLoadedMetadata={() => {
                  console.log('[SwingAnalysisCanon] Video metadata loaded');
                }}
                onError={(e) => {
                  console.error('[SwingAnalysisCanon] Video error:', e);
                }}
              />
              {POSE_OVERLAY_ANALYSIS_ENABLED && showOverlay && poseData && (
                <div className="absolute inset-0 pointer-events-none z-10">
                  <OverlayCanvas
                    videoRef={videoRef}
                    getLatestFrame={getLatestFrame}
                    getFrameAtTime={getFrameAtTimeFromPose}
                    isLiveCapture={false}
                    visible={true}
                    opacity={overlayOpacity}
                    showSkeleton={true}
                    handsOnly={false}
                    replayDirectMap
                  />
                </div>
              )}
              {!videoUrl && (
                <div className="absolute inset-0 w-full h-full bg-gray-800 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">No video available</span>
                </div>
              )}
            </div>
            {/* Pose overlay control bar — hidden until overlay is stable (see docs/POSE_OVERLAY_ANALYSIS_PAGE.md) */}
            {POSE_OVERLAY_ANALYSIS_ENABLED && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border-t border-slate-200 rounded-b-xl">
              <span className="text-sm font-medium text-slate-700">
                Pose overlay
                {!poseData && <span className="ml-2 text-slate-400 font-normal">(not available for this run)</span>}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!poseData}
                onClick={() => poseData && setShowOverlay((v) => !v)}
                aria-label={poseData ? (showOverlay ? 'Hide pose overlay' : 'Show pose overlay') : 'Pose overlay not available'}
                aria-pressed={showOverlay}
              >
                {showOverlay ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Overlay
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Overlay
                  </>
                )}
              </Button>
            </div>
            )}
          </div>
        </div>

        {!canonicalData ? (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
            <p className="text-muted-foreground">Loading analysis...</p>
          </div>
        ) : (
          <>
        {/* Key Opportunity Card - Enhanced with Fault Frame */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6 text-[#0D382A]" />
            </div>
            <h3 className="text-[#0D382A] text-xl">Your Key Opportunity</h3>
          </div>
          
          {hasPrimaryFault ? (
            <>
              {/* Two-column layout: Frame + Text */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {/* LEFT: Fault Frame (40%) - ALWAYS RENDER with placeholder if no image */}
                <div className="md:col-span-2">
                  <div 
                    className="relative rounded-xl overflow-hidden border-2 border-transparent bg-clip-padding cursor-pointer group"
                    onClick={handleFaultFrameClick}
                  >
                    {/* Gradient border */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] opacity-100 group-hover:opacity-80 transition-opacity duration-200 -z-10" style={{ padding: '2px' }}>
                      <div className="absolute inset-[2px] bg-white rounded-xl" />
                    </div>
                    
                    {/* Frame image or placeholder */}
                    <div className="relative z-10 m-[2px] rounded-lg overflow-hidden">
                      {faultFrame?.imageUrl ? (
                        <img 
                          src={signedFaultFrameUrl || faultFrame.imageUrl} 
                          alt="Fault frame"
                          className="w-full aspect-[4/3] object-cover"
                        />
                      ) : (
                        /* Placeholder when no image */
                        <div className="w-full aspect-[4/3] bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                          <div className="text-gray-400 text-sm">Frame capture</div>
                        </div>
                      )}
                      
                      {/* Fault overlay - same as video */}
                      <div className="absolute inset-0 pointer-events-none">
                        {(faultFrame?.region || primaryFault.region) === 'impact' && (
                          <div className="absolute right-1/3 top-1/2 -translate-y-1/2 w-24 h-24">
                            <div className="w-full h-full rounded-full border-2 border-[#196644] opacity-60 animate-pulse" />
                          </div>
                        )}
                        {(faultFrame?.region || primaryFault.region) === 'downswing' && (
                          <div className="absolute right-1/3 top-1/3 w-24 h-24">
                            <div className="w-full h-full rounded-full border-2 border-[#196644] opacity-60 animate-pulse" />
                          </div>
                        )}
                        {(faultFrame?.region || primaryFault.region) === 'backswing' && (
                          <div className="absolute left-1/4 top-1/3 w-24 h-24">
                            <div className="w-full h-full rounded-full border-2 border-[#196644] opacity-60 animate-pulse" />
                          </div>
                        )}
                        {(faultFrame?.region || primaryFault.region) === 'follow-through' && (
                          <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-24 h-24">
                            <div className="w-full h-full rounded-full border-2 border-[#196644] opacity-60 animate-pulse" />
                          </div>
                        )}
                      </div>
                      
                      {/* Play icon overlay on hover */}
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-6 h-6 text-[#0D382A] ml-0.5" fill="#0D382A" />
                        </div>
                      </div>
                      
                      {/* Timestamp badge - always visible */}
                      <div className="absolute bottom-2 left-2 px-3 py-1 bg-[#0D382A]/80 backdrop-blur-sm rounded-full">
                        <span className="text-white text-xs uppercase tracking-wide">
                          {(faultFrame?.region || primaryFault.region).toUpperCase()} · {faultFrame?.timestamp || '00:00:01.500'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Helper text below frame */}
                  <div className="mt-2 flex items-center justify-center gap-2">
                    <p className="text-gray-500 text-xs text-center">
                      Click to watch in full video ↑
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        refreshFaultFrame();
                      }}
                      disabled={refreshingFaultFrame}
                      className="text-xs px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {refreshingFaultFrame ? 'Refreshing…' : 'Refresh frame'}
                    </button>
                  </div>
                </div>
                
                {/* RIGHT: Text (60%) */}
                <div className="md:col-span-3 flex flex-col justify-center">
                  {/* PLA-111: LLM-generated focus title (primary) with fallback to canonical name */}
                  <h4 className="text-[#0D382A] text-lg font-bold mb-2">
                    {primaryFault.coachingFocusTitle || primaryFault.canonicalName}
                  </h4>
                  
                  {/* PLA-94: Contextual banner for phase-evidence-only renders */}
                  {canonicalData.isPhaseEvidenceOnly && (
                    <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-blue-700 text-sm">
                        This pattern is identified from motion behavior rather than a single underlying root cause.
                      </p>
                    </div>
                  )}
                  
                  {/* Headline from V3 root_causes */}
                  {primaryFault.headline && (
                    <p className="text-[#0D382A] text-base mb-2">{primaryFault.headline}</p>
                  )}
                  {/* Description from V3 root_causes */}
                  {primaryFault.description && (
                    <p className="text-gray-600">
                      {primaryFault.description}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : canonicalData.isExecutionOnly ? (
            /* PLA-117: Execution-only swing - no dominant mechanical fault, but coaching still flows */
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <h4 className="text-[#0D382A] text-lg font-bold">
                  {canonicalData.executionCoaching?.focusTitle || 'High Quality Swing'}
                </h4>
              </div>
              
              {canonicalData.executionCoaching?.headline && (
                <p className="text-[#0D382A] text-base">
                  {canonicalData.executionCoaching.headline}
                </p>
              )}
              
              {canonicalData.executionCoaching?.description && (
                <p className="text-gray-600">
                  {canonicalData.executionCoaching.description}
                </p>
              )}
              
              {!canonicalData.executionCoaching?.headline && !canonicalData.executionCoaching?.description && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">
                    No dominant mechanical fault was identified. Keep building consistency with intent.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* PLA-89: Empty state - analysis not renderable per V3 contract */
            <div className="space-y-3">
              <h4 className="text-[#0D382A] text-lg font-bold">Analysis unavailable</h4>
              <p className="text-gray-600">
                This swing was processed, but did not produce enough structured insight to display a full analysis.
              </p>
              {canonicalData.scoreInterpretation && (
                <div className="mt-4">
                  <p className="text-[#0D382A] text-sm font-medium mb-2">What we're seeing</p>
                  <p className="text-gray-600">
                    {canonicalData.scoreInterpretation}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PLA-89: Section-level conditional rendering - each section renders independently based on its own data */}
        {/* Positive Observations Card - Only render if V3 data exists */}
        {canonicalData.positiveObservations.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-[#0D382A] text-xl">Positive Observations</h3>
            </div>
            <div className="space-y-3">
              {canonicalData.positiveObservations.map((observation, index) => (
                <div key={index} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-600">{observation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Why This Happens Card - PLA-115: Only renders if why_priority exists (reference-only content)
            This prevents duplication with Key Opportunity which shows the full coaching.summary */}
        {(
          (hasPrimaryFault && primaryFault.explanation.why.length > 0) ||
          (canonicalData.isExecutionOnly && typeof execWhyPriority === 'string' && execWhyPriority.trim().length > 0)
        ) && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-[#0D382A] text-xl">Why This Happens</h3>
            </div>
            <div className="space-y-3">
              {(hasPrimaryFault ? primaryFault.explanation.why : [execWhyPriority as string]).map((reason, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#196644]/10 flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-[#196644]" />
                  </div>
                  <p className="text-gray-600">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How It Affects Your Swing Card - Only render if V3 data exists */}
        {hasPrimaryFault && primaryFault.explanation.affects.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-[#0D382A] text-xl">How It Affects Your Swing</h3>
            </div>
            <ul className="space-y-2 ml-4">
              {primaryFault.explanation.affects.map((affect, index) => (
                <li key={index} className="text-gray-600 flex gap-2">
                  <span className="text-orange-600">•</span>
                  <span>{affect}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Impact Card - Why This Costs You Strokes */}
        {(
          (hasPrimaryFault && canonicalData.scoringImpact && canonicalData.scoringImpact.impactItems.length > 0) ||
          (canonicalData.isExecutionOnly && typeof execImpactIfAddressed === 'string' && execImpactIfAddressed.trim().length > 0)
        ) && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#A7F3A1] flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-[#0D382A]" />
              </div>
              <div className="flex-1">
                <h3 className="text-[#0D382A] text-xl mb-4">Why This Costs You Strokes</h3>
                {/* Render as paragraph (synthesized text) instead of list */}
                <p className="text-gray-600 leading-relaxed">
                  {hasPrimaryFault
                    ? (canonicalData.scoringImpact?.impactItems?.[0] || '')
                    : (execImpactIfAddressed as string)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Recommended Next Steps (execution-only only; no legacy drill content) */}
        {canonicalData.isExecutionOnly && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] border-2 border-transparent bg-clip-padding relative overflow-hidden">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] opacity-30 -z-10" style={{ padding: '2px' }}>
            <div className="absolute inset-[2px] bg-white rounded-2xl" />
          </div>

          <div className="relative z-10">
            <h3 className="text-[#0D382A] text-xl mb-4">Recommended Next Steps</h3>
            
            <div className="space-y-5">
              {canonicalData.executionCoaching?.swingThoughts?.length ? (
                <div>
                  <p className="text-[#0D382A] text-sm font-medium mb-2">Swing thoughts</p>
                  <ul className="space-y-2">
                    {canonicalData.executionCoaching.swingThoughts.slice(0, 5).map((t, idx) => (
                      <li key={idx} className="text-gray-600 flex gap-2">
                        <span className="text-[#196644]">•</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {canonicalData.executionCoaching?.practicePlan ? (
                <div>
                  <p className="text-[#0D382A] text-sm font-medium mb-2">Practice plan</p>
                  {canonicalData.executionCoaching.practicePlan.focusAreas.length > 0 && (
                    <p className="text-gray-600 text-sm">
                      Focus: {canonicalData.executionCoaching.practicePlan.focusAreas.slice(0, 2).join(' • ')}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}

